import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL, { API_ENDPOINTS, buildApiUrl } from './config/api';
import './AdminDashboard.css';
import { printAgreementPdf } from './utils/printAgreement';

function AdminDashboard({ adminData, onLogout }) {
  // Extract token for use throughout component and child components
  const adminToken = adminData.token;

  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [goals, setGoals] = useState([]);
  const [progressNotes, setProgressNotes] = useState([]);
  const [intakeData, setIntakeData] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [creationStep, setCreationStep] = useState(1);
  const [newUserData, setNewUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    notes: ''
  });
  const [newUserAgreement, setNewUserAgreement] = useState({
    packageName: '',
    cadence: '',
    startingDate: '',
    endingDate: '',
    fee: '',
    coachingFocus: '',
    paymentAmount: ''
  });
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const contractEditorRef = useRef(null);
  const [emailConflict, setEmailConflict] = useState(false);
  const [packageTemplates, setPackageTemplates] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nl_package_templates') || '[]'); }
    catch { return []; }
  });
  const [newTemplateName, setNewTemplateName] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({ firstName: '', lastName: '', email: '' });

  // Load users on component mount
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load user-specific data when selectedUser changes
  useEffect(() => {
    if (selectedUser) {
      fetchUserAssessments(selectedUser._id);
      fetchUserGoals(selectedUser._id);
      fetchUserProgressNotes(selectedUser._id);
      fetchUserIntakeData(selectedUser._id);
      fetchUserContracts(selectedUser._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  // Populate contentEditable editor when preview HTML is ready
  useEffect(() => {
    if (creationStep === 3 && contractEditorRef.current && previewHtml) {
      contractEditorRef.current.innerHTML = previewHtml;
    }
  }, [creationStep, previewHtml]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USERS, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
    setLoading(false);
  };

  const fetchUserAssessments = async (userId) => {
    try {
      const response = await fetch(buildApiUrl('/api/admin/users/:userId/assessments', { userId }), {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAssessments(data);
      }
    } catch (error) {
      console.error('Error fetching assessments:', error);
    }
  };


  const fetchUserGoals = async (userId) => {
    try {
      const response = await fetch(buildApiUrl('/api/admin/users/:userId/goals', { userId }), {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setGoals(data);
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
    }
  };

  const fetchUserProgressNotes = async (userId) => {
    try {
      const response = await fetch(buildApiUrl('/api/admin/users/:userId/progress-notes', { userId }), {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setProgressNotes(data);
      }
    } catch (error) {
      console.error('Error fetching progress notes:', error);
    }
  };

  const fetchUserIntakeData = async (userId) => {
    try {
      const response = await fetch(buildApiUrl('/api/admin/users/:userId/intake', { userId }), {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setIntakeData(data.intakeData);
      } else if (response.status === 404) {
        setIntakeData(null);
      }
    } catch (error) {
      console.error('Error fetching intake data:', error);
      setIntakeData(null);
    }
  };

  const fetchUserContracts = async (userId) => {
    try {
      const response = await fetch(buildApiUrl('/api/admin/users/:userId/contracts', { userId }), {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  // Refresh selected user data (used after updates)
  const refreshSelectedUser = async (userId) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN_USERS}/${userId}`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      if (response.ok) {
        const updatedUser = await response.json();
        setSelectedUser(updatedUser);

        // Also update in the users list
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user._id === userId ? updatedUser : user
          )
        );

        console.log('Selected user refreshed:', updatedUser);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    // Auto-refresh data when switching tabs
    if (selectedUser) {
      if (tabId === 'assessments') {
        fetchUserAssessments(selectedUser._id);
      } else if (tabId === 'progress') {
        fetchUserGoals(selectedUser._id);
        fetchUserProgressNotes(selectedUser._id);
      } else if (tabId === 'intake') {
        fetchUserIntakeData(selectedUser._id);
      } else if (tabId === 'contracts') {
        fetchUserContracts(selectedUser._id);
      } else if (tabId === 'scheduling') {
        // Refresh user data to get latest session allowance
        refreshSelectedUser(selectedUser._id);
      }
    }
  };

  const handleStep1Next = (e) => {
    e.preventDefault();
    if (emailConflict) return;
    setCreationStep(2);
  };

  const handlePreviewAgreement = async (e) => {
    e.preventDefault();
    setLoadingPreview(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/contract-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          firstName: newUserData.firstName,
          lastName: newUserData.lastName,
          email: newUserData.email,
          contractTerms: newUserAgreement
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPreviewHtml(data.html);
        setCreationStep(3);
      } else {
        alert(`Failed to load preview: ${data.message}`);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      alert('Failed to load agreement preview. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleCreateNewUser = async () => {
    const finalHtml = contractEditorRef.current?.innerHTML || previewHtml;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          ...newUserData,
          contractTerms: newUserAgreement,
          contractHtml: finalHtml
        })
      });

      const data = await response.json();

      if (response.ok) {
        setCreatedUser(data.user);
        setCreationStep(4);
      } else {
        alert(`Failed to create user: ${data.message}`);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user. Please try again.');
    }
  };

  const handleCloseNewUserModal = () => {
    setShowNewUserModal(false);
    setCreationStep(1);
    setCreatedUser(null);
    setPreviewHtml('');
    setEmailConflict(false);
    setNewUserData({
      firstName: '',
      lastName: '',
      email: '',
      notes: ''
    });
    setNewUserAgreement({
      packageName: '',
      cadence: '',
      startingDate: '',
      endingDate: '',
      fee: '',
      coachingFocus: '',
      paymentAmount: ''
    });
    fetchUsers();
  };

  const handleDeleteUser = async (user, e) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      `Permanently delete ${user.firstName} ${user.lastName} (${user.email})?\n\nThis will remove the user and ALL their data including assessments, goals, session notes, and contracts. This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${user._id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (response.ok) {
        if (selectedUser?._id === user._id) setSelectedUser(null);
        fetchUsers();
      } else {
        const data = await response.json();
        alert(`Failed to delete user: ${data.message}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Please try again.');
    }
  };

  const handleLoadTemplate = (templateName) => {
    const t = packageTemplates.find(t => t.name === templateName);
    if (!t) return;
    setNewUserAgreement({
      packageName: t.packageName,
      cadence: t.cadence,
      startingDate: t.startingDate,
      endingDate: t.endingDate,
      fee: t.fee,
      coachingFocus: t.coachingFocus
    });
  };

  const handleSaveTemplate = () => {
    if (!newTemplateName.trim()) {
      alert('Enter a name for this template');
      return;
    }
    const template = { name: newTemplateName.trim(), ...newUserAgreement };
    const updated = [...packageTemplates.filter(t => t.name !== template.name), template];
    setPackageTemplates(updated);
    localStorage.setItem('nl_package_templates', JSON.stringify(updated));
    setNewTemplateName('');
  };

  const handleDeleteTemplate = (name, e) => {
    e.stopPropagation();
    const updated = packageTemplates.filter(t => t.name !== name);
    setPackageTemplates(updated);
    localStorage.setItem('nl_package_templates', JSON.stringify(updated));
  };

  const handleOpenEditUser = (user, e) => {
    e.stopPropagation();
    setEditingUser(user);
    setEditUserData({ firstName: user.firstName, lastName: user.lastName, email: user.email });
  };

  const handleSaveEditUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${editingUser._id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(editUserData)
      });
      const data = await response.json();
      if (response.ok) {
        setEditingUser(null);
        fetchUsers();
        if (selectedUser?._id === editingUser._id) refreshSelectedUser(editingUser._id);
      } else {
        alert(`Failed to update: ${data.message}`);
      }
    } catch (error) {
      alert('Failed to update user. Please try again.');
    }
  };

  const TabButton = ({ tabId, label, isActive, onClick }) => (
    <button
      className={`tab-button ${isActive ? 'active' : ''}`}
      onClick={() => onClick(tabId)}
    >
      {label}
    </button>
  );

  const pendingCount = users.filter(u => u.registrationStatus === 'pre-registered').length;

  const UserList = () => (
    <div className="user-list">
      <div className="section-header">
        <div>
          <h3>All Users</h3>
          {pendingCount > 0 && (
            <p className="pending-registration-count">{pendingCount} pending registration</p>
          )}
        </div>
        <button
          onClick={() => setShowNewUserModal(true)}
          className="btn btn-primary"
        >
          + Create New User
        </button>
      </div>
      {loading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <div className="users-grid">
          {users.map(user => (
            <div
              key={user._id}
              className={`user-card ${selectedUser?._id === user._id ? 'selected' : ''}`}
              onClick={() => setSelectedUser(user)}
            >
              <div className="user-card-header">
                <h4>{user.firstName} {user.lastName}</h4>
                <div className="user-card-actions">
                  {user.registrationStatus === 'pre-registered' && (
                    <button
                      className="btn-edit-user"
                      title="Edit user"
                      onClick={(e) => handleOpenEditUser(user, e)}
                    >
                      ✎
                    </button>
                  )}
                  <button
                    className="btn-delete-user"
                    title="Delete user"
                    onClick={(e) => handleDeleteUser(user, e)}
                  >
                    &times;
                  </button>
                </div>
              </div>
              <p>{user.email}</p>
              <p>Experience: {user.experienceLevel || 'Not set'}</p>
              <p>Registered: {new Date(user.registrationDate || user.createdAt).toLocaleDateString()}</p>
              <div className="intake-status">
                {user.registrationStatus === 'pre-registered' ? (
                  <span className="status-badge pre-registered">Pending Registration</span>
                ) : (
                  <span className="status-badge registered">Registered</span>
                )}
                {' '}
                {user.hasCompletedIntake ? (
                  <span className="status-badge completed">Intake Complete</span>
                ) : (
                  <span className="status-badge pending">Intake Pending</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-dashboard">
      {/* Edit Pre-Registered User Modal */}
      {editingUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-step-header">
              <h4>Edit User</h4>
              <p className="modal-step-subtitle">Update details for this pending registration.</p>
            </div>
            <form onSubmit={handleSaveEditUser}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={editUserData.firstName}
                    onChange={(e) => setEditUserData({ ...editUserData, firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={editUserData.lastName}
                    onChange={(e) => setEditUserData({ ...editUserData, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={editUserData.email}
                  onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setEditingUser(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New User Modal — multi-step */}
      {showNewUserModal && (
        <div className="modal-overlay">
          <div className={`modal ${creationStep === 3 ? 'modal-large' : ''}`}>

            {/* Step 1: Basic info */}
            {creationStep === 1 && (
              <>
                <div className="modal-step-header">
                  <span className="modal-step-label">Step 1 of 3 — Client Info</span>
                  <h4>Create New User</h4>
                </div>
                <form onSubmit={handleStep1Next}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        value={newUserData.firstName}
                        onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
                        placeholder="First name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        value={newUserData.lastName}
                        onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
                        placeholder="Last name"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={newUserData.email}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewUserData({ ...newUserData, email: val });
                        setEmailConflict(users.some(u => u.email.toLowerCase() === val.toLowerCase().trim()));
                      }}
                      placeholder="email@example.com"
                      required
                      style={emailConflict ? { borderColor: '#dc3545' } : {}}
                    />
                    {emailConflict && (
                      <p className="field-error">A user with this email already exists.</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Notes (optional)</label>
                    <textarea
                      value={newUserData.notes}
                      onChange={(e) => setNewUserData({ ...newUserData, notes: e.target.value })}
                      rows="2"
                      placeholder="Internal notes about this user..."
                    />
                  </div>

                  <div className="modal-actions">
                    <button type="button" onClick={handleCloseNewUserModal}>Cancel</button>
                    <button type="submit" className="btn-primary" disabled={emailConflict}>Next: Agreement Details →</button>
                  </div>
                </form>
              </>
            )}

            {/* Step 2: Agreement details */}
            {creationStep === 2 && (
              <>
                <div className="modal-step-header">
                  <span className="modal-step-label">Step 2 of 3 — Coaching Agreement</span>
                  <h4>Fill In Agreement Details</h4>
                  <p className="modal-step-subtitle">These values will be auto-populated in the coaching agreement that <strong>{newUserData.firstName} {newUserData.lastName}</strong> sees.</p>
                </div>

                {packageTemplates.length > 0 && (
                  <div className="template-selector">
                    <label>Load a saved template</label>
                    <div className="template-selector-row">
                      <select
                        defaultValue=""
                        onChange={(e) => { if (e.target.value) handleLoadTemplate(e.target.value); e.target.value = ''; }}
                      >
                        <option value="" disabled>Choose template…</option>
                        {packageTemplates.map(t => (
                          <option key={t.name} value={t.name}>{t.name}</option>
                        ))}
                      </select>
                      {packageTemplates.map(t => (
                        <button
                          key={t.name}
                          type="button"
                          className="btn-delete-template"
                          title={`Delete "${t.name}"`}
                          onClick={(e) => handleDeleteTemplate(t.name, e)}
                        >
                          ×
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <form onSubmit={handlePreviewAgreement}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Coaching Package Name *</label>
                      <input
                        type="text"
                        value={newUserAgreement.packageName}
                        onChange={(e) => setNewUserAgreement({ ...newUserAgreement, packageName: e.target.value })}
                        placeholder="e.g., Executive Leadership — 6 Sessions"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Coaching Cadence *</label>
                      <input
                        type="text"
                        value={newUserAgreement.cadence}
                        onChange={(e) => setNewUserAgreement({ ...newUserAgreement, cadence: e.target.value })}
                        placeholder="e.g., bi-weekly, weekly"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Starting Date *</label>
                      <input
                        type="text"
                        value={newUserAgreement.startingDate}
                        onChange={(e) => setNewUserAgreement({ ...newUserAgreement, startingDate: e.target.value })}
                        placeholder="e.g., June 1, 2026"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Ending Date *</label>
                      <input
                        type="text"
                        value={newUserAgreement.endingDate}
                        onChange={(e) => setNewUserAgreement({ ...newUserAgreement, endingDate: e.target.value })}
                        placeholder="e.g., September 1, 2026"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Fee *</label>
                      <input
                        type="text"
                        value={newUserAgreement.fee}
                        onChange={(e) => setNewUserAgreement({ ...newUserAgreement, fee: e.target.value })}
                        placeholder="e.g., $300/month or $150 per session"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Initial Payment Amount ($) *</label>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        value={newUserAgreement.paymentAmount}
                        onChange={(e) => setNewUserAgreement({ ...newUserAgreement, paymentAmount: e.target.value })}
                        placeholder="e.g., 300"
                        required
                      />
                      <small>Amount charged via Stripe during onboarding</small>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Coaching Focus *</label>
                    <textarea
                      value={newUserAgreement.coachingFocus}
                      onChange={(e) => setNewUserAgreement({ ...newUserAgreement, coachingFocus: e.target.value })}
                      rows="2"
                      placeholder="e.g., Leadership Development, Executive Presence, Strategic Communication"
                      required
                    />
                  </div>

                  <div className="save-template-row">
                    <input
                      type="text"
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                      placeholder="Template name (e.g. 6-Session Executive)"
                    />
                    <button type="button" className="btn-save-template" onClick={handleSaveTemplate}>
                      Save as Template
                    </button>
                  </div>

                  <div className="modal-actions">
                    <button type="button" onClick={() => setCreationStep(1)}>← Back</button>
                    <button type="submit" className="btn-primary" disabled={loadingPreview}>
                      {loadingPreview ? 'Loading Preview...' : 'Preview Agreement →'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* Step 3: Agreement preview + inline editing */}
            {creationStep === 3 && (
              <>
                <div className="modal-step-header">
                  <span className="modal-step-label">Step 3 of 3 — Review &amp; Edit Agreement</span>
                  <h4>Review Agreement for {newUserData.firstName} {newUserData.lastName}</h4>
                  <p className="modal-step-subtitle">Click anywhere in the agreement to make edits. Changes are saved when you confirm.</p>
                </div>
                <div className="contract-editor-hint">✏ Click to edit any text in the agreement below</div>
                <div
                  ref={contractEditorRef}
                  contentEditable={true}
                  suppressContentEditableWarning={true}
                  className="contract-editor"
                />
                <div className="modal-actions">
                  <button type="button" onClick={() => setCreationStep(2)}>← Edit Details</button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleCreateNewUser}
                  >
                    Confirm &amp; Create User
                  </button>
                </div>
              </>
            )}

            {/* Step 4: Success */}
            {creationStep === 4 && (
              <>
                <div className="modal-step-header">
                  <h4>User Created!</h4>
                </div>
                <div className="access-code-display">
                  <div className="success-message" style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                    ✓ Account registered successfully
                  </div>
                  <p><strong>{createdUser?.firstName} {createdUser?.lastName}</strong></p>
                  <p style={{ color: '#6b7280' }}>{createdUser?.email}</p>
                  <p style={{ marginTop: '1rem', color: '#374151' }}>
                    They can now go to the platform, click <strong>Create Account</strong>, and enter their email to set up a password.
                  </p>
                </div>
                <div className="modal-actions">
                  <button onClick={handleCloseNewUserModal} className="btn-primary">Done</button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

      <header className="admin-header">
        <div className="admin-brand">
          <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
            <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="admin-logo" />
          </a>
          <h1>Admin Dashboard</h1>
        </div>
        <div className="admin-actions">
          <span>Welcome, Admin</span>
          <button onClick={onLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      <div className="admin-content">
        <nav className="admin-tabs">
          <TabButton 
            tabId="users" 
            label="Users" 
            isActive={activeTab === 'users'} 
            onClick={handleTabClick} 
          />
          <TabButton 
            tabId="intake" 
            label="Intake Form" 
            isActive={activeTab === 'intake'} 
            onClick={handleTabClick} 
          />
          <TabButton 
            tabId="assessments" 
            label="Assessments" 
            isActive={activeTab === 'assessments'} 
            onClick={handleTabClick} 
          />
          <TabButton 
            tabId="progress" 
            label="Session Prep and Notes" 
            isActive={activeTab === 'progress'} 
            onClick={handleTabClick} 
          />
          <TabButton 
            tabId="contracts" 
            label="Contracts" 
            isActive={activeTab === 'contracts'} 
            onClick={handleTabClick} 
          />
        </nav>

        <main className="admin-main">
          {activeTab === 'users' && <UserList />}
          
          {activeTab === 'assessments' && (
            <AssessmentManager 
              selectedUser={selectedUser} 
              assessments={assessments}
              onAssessmentUpdate={fetchUserAssessments}
              adminToken={adminToken}
            />
          )}


          {activeTab === 'progress' && (
            <ProgressManager 
              selectedUser={selectedUser} 
              goals={goals}
              progressNotes={progressNotes}
              onProgressUpdate={() => {
                if (selectedUser) {
                  fetchUserGoals(selectedUser._id);
                  fetchUserProgressNotes(selectedUser._id);
                }
              }}
              adminToken={adminToken}
            />
          )}

          {activeTab === 'intake' && (
            <IntakeViewer 
              selectedUser={selectedUser} 
              intakeData={intakeData}
            />
          )}

          {activeTab === 'contracts' && (
            <ContractManager 
              selectedUser={selectedUser} 
              contracts={contracts}
              onContractUpdate={() => {
                if (selectedUser) {
                  fetchUserContracts(selectedUser._id);
                }
              }}
              adminToken={adminToken}
            />
          )}

        </main>
      </div>
    </div>
  );
}

// Assessment Management Component
function AssessmentManager({ selectedUser, assessments, onAssessmentUpdate, adminToken }) {
  const [showNewAssessment, setShowNewAssessment] = useState(false);
  const [showAssignAssessment, setShowAssignAssessment] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editingAssessmentAdminNote, setEditingAssessmentAdminNote] = useState(null);
  const [assessmentAdminNoteText, setAssessmentAdminNoteText] = useState('');
  const [newAssessment, setNewAssessment] = useState({
    title: '',
    category: 'Leadership',
    description: '',
    dateCompleted: '',
    status: 'scheduled',
    resultUrl: '',
    notes: '',
    type: 'custom' // Use custom type like user form
  });
  const [assignAssessment, setAssignAssessment] = useState({
    title: '',
    category: 'Leadership',
    description: '',
    instructions: '',
    dueDate: '',
    assessmentUrl: ''
  });
  const [adminEditForm, setAdminEditForm] = useState({
    title: '',
    category: '',
    description: '',
    resultUrl: '',
    notes: '',
    dateCompleted: '',
    status: '',
    adminNotes: ''
  });

  const categories = [
    'Leadership',
    'Personality & Behavior',
    'Strengths',
    'Values & Motivation',
    'Emotional Intelligence',
    'Skills & Competencies',
    'Communication',
    'Other'
  ];

  const createAssessment = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      alert('Please select a user first');
      return;
    }

    try {
      console.log('Admin creating assessment for user:', selectedUser);
      
      const response = await fetch(API_ENDPOINTS.ADMIN_ASSESSMENTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          client: selectedUser._id,
          type: 'custom',
          status: newAssessment.status,
          results: {
            summary: newAssessment.description,
            recommendations: newAssessment.notes ? [newAssessment.notes] : []
          },
          coachNotes: `Admin added assessment: ${newAssessment.title}`,
          completedDate: newAssessment.status === 'completed' && newAssessment.dateCompleted 
            ? new Date(newAssessment.dateCompleted) 
            : null,
          customData: {
            title: newAssessment.title,
            category: newAssessment.category,
            description: newAssessment.description,
            resultUrl: newAssessment.resultUrl,
            notes: newAssessment.notes
          }
        })
      });

      if (response.ok) {
        setShowNewAssessment(false);
        setNewAssessment({
          title: '',
          category: 'Leadership',
          description: '',
          dateCompleted: '',
          status: 'scheduled',
          resultUrl: '',
          notes: '',
          type: 'custom'
        });
        onAssessmentUpdate(selectedUser._id);
      } else {
        const errorData = await response.json();
        alert(`Failed to create assessment: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error creating assessment:', error);
      alert('Failed to create assessment. Please try again.');
    }
  };

  const assignAssessmentToUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      alert('Please select a user first');
      return;
    }

    try {
      console.log('Admin assigning assessment to user:', selectedUser);
      
      const response = await fetch(API_ENDPOINTS.ADMIN_ASSESSMENTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          client: selectedUser._id,
          type: 'custom',
          status: 'pending',
          assignedDate: new Date(),
          dueDate: assignAssessment.dueDate ? new Date(assignAssessment.dueDate) : null,
          results: {
            summary: assignAssessment.description,
            instructions: assignAssessment.instructions
          },
          coachNotes: `Admin assigned assessment: ${assignAssessment.title}`,
          customData: {
            title: assignAssessment.title,
            category: assignAssessment.category,
            description: assignAssessment.description,
            instructions: assignAssessment.instructions,
            assessmentUrl: assignAssessment.assessmentUrl,
            isPending: true
          }
        })
      });

      if (response.ok) {
        setShowAssignAssessment(false);
        setAssignAssessment({
          title: '',
          category: 'Leadership',
          description: '',
          instructions: '',
          dueDate: '',
          assessmentUrl: ''
        });
        onAssessmentUpdate(selectedUser._id);
        alert('Assessment assigned successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to assign assessment: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error assigning assessment:', error);
      alert('Failed to assign assessment. Please try again.');
    }
  };

  const openEditModal = (assessment) => {
    setEditingAssessment(assessment);
    setAdminEditForm({
      title: assessment.customData?.title || assessment.type,
      category: assessment.customData?.category || 'Leadership',
      description: assessment.customData?.description || assessment.results?.summary || '',
      resultUrl: assessment.customData?.resultUrl || '',
      notes: assessment.customData?.notes || assessment.results?.recommendations?.[0] || '',
      dateCompleted: assessment.completedDate ? assessment.completedDate.split('T')[0] : '',
      status: assessment.status,
      adminNotes: assessment.adminNotes || ''
    });
    setShowEditModal(true);
  };

  const updateAssessmentAdmin = async (e) => {
    e.preventDefault();
    if (!editingAssessment) return;

    try {
      const response = await fetch(buildApiUrl('/api/assessments/:id', { id: editingAssessment._id }), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          status: adminEditForm.status,
          completedDate: adminEditForm.status === 'completed' && adminEditForm.dateCompleted
            ? new Date(adminEditForm.dateCompleted)
            : null,
          customData: {
            title: adminEditForm.title,
            category: adminEditForm.category,
            description: adminEditForm.description,
            resultUrl: adminEditForm.resultUrl,
            notes: adminEditForm.notes
          },
          results: {
            summary: adminEditForm.description,
            recommendations: adminEditForm.notes ? [adminEditForm.notes] : []
          },
          adminNotes: adminEditForm.adminNotes
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditingAssessment(null);
        setAdminEditForm({
          title: '',
          category: '',
          description: '',
          resultUrl: '',
          notes: '',
          dateCompleted: '',
          status: '',
          adminNotes: ''
        });
        onAssessmentUpdate(selectedUser._id);
      } else {
        const errorData = await response.json();
        alert(`Failed to update assessment: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error updating assessment:', error);
      alert('Failed to update assessment. Please try again.');
    }
  };

  const quickStatusUpdate = async (assessmentId, newStatus) => {
    try {
      const response = await fetch(buildApiUrl('/api/admin/assessments/:id', { id: assessmentId }), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        onAssessmentUpdate(selectedUser._id);
      } else {
        console.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleEditAssessmentAdminNote = (assessmentId, currentAdminNotes) => {
    setEditingAssessmentAdminNote(assessmentId);
    setAssessmentAdminNoteText(currentAdminNotes || '');
  };

  const handleCancelAssessmentAdminNote = () => {
    setEditingAssessmentAdminNote(null);
    setAssessmentAdminNoteText('');
  };

  const handleSaveAssessmentAdminNote = async (assessmentId) => {
    try {
      const response = await fetch(buildApiUrl('/api/assessments/:id', { id: assessmentId }), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          adminNotes: assessmentAdminNoteText
        })
      });

      if (response.ok) {
        setEditingAssessmentAdminNote(null);
        setAssessmentAdminNoteText('');
        await onAssessmentUpdate(selectedUser._id);
      } else {
        console.error('Failed to save assessment admin note');
        alert('Failed to save admin note');
      }
    } catch (error) {
      console.error('Error saving assessment admin note:', error);
      alert('Failed to save admin note');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await onAssessmentUpdate(selectedUser._id);
    // Small delay for visual feedback
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleDeleteAssessment = async (assessmentId) => {
    if (window.confirm('Are you sure you want to delete this assessment? This action cannot be undone.')) {
      try {
        const response = await fetch(buildApiUrl('/api/admin/assessments/:assessmentId', { assessmentId }), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });

        if (response.ok) {
          await onAssessmentUpdate(selectedUser._id);
          alert('Assessment deleted successfully');
        } else {
          const errorData = await response.json();
          alert(`Failed to delete assessment: ${errorData.message}`);
        }
      } catch (error) {
        console.error('Error deleting assessment:', error);
        alert('Failed to delete assessment. Please try again.');
      }
    }
  };

  if (!selectedUser) {
    return (
      <div className="no-selection">
        <p>Select a user from the Users tab to manage their assessments</p>
      </div>
    );
  }

  return (
    <div className="assessment-manager">
      <div className="section-header">
        <h3>Assessments for {selectedUser.firstName} {selectedUser.lastName}</h3>
        <div className="section-actions">
          <button 
            onClick={handleRefresh}
            className={`btn btn-outline refresh-btn ${refreshing ? 'refreshing' : ''}`}
            title="Refresh assessments"
            disabled={refreshing}
          >
            {refreshing ? '⟳ Refreshing...' : '↻ Refresh'}
          </button>
          <button 
            onClick={() => setShowNewAssessment(true)}
            className="btn btn-primary"
          >
            Add Assessment Results
          </button>
          <button 
            onClick={() => setShowAssignAssessment(true)}
            className="btn btn-secondary"
          >
            Assign Assessment
          </button>
        </div>
      </div>

      {showNewAssessment && (
        <div className="modal-overlay">
          <div className="modal">
            <h4>Add Assessment Results</h4>
            <form onSubmit={createAssessment}>
              <div className="form-group">
                <label>Assessment Title *</label>
                <input
                  type="text"
                  value={newAssessment.title}
                  onChange={(e) => setNewAssessment({...newAssessment, title: e.target.value})}
                  placeholder="e.g., Leadership 360 Assessment"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={newAssessment.category}
                    onChange={(e) => setNewAssessment({...newAssessment, category: e.target.value})}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={newAssessment.status}
                    onChange={(e) => setNewAssessment({...newAssessment, status: e.target.value})}
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newAssessment.description}
                  onChange={(e) => setNewAssessment({...newAssessment, description: e.target.value})}
                  rows="3"
                  placeholder="Brief description of what this assessment measures"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date Completed (if applicable)</label>
                  <input
                    type="date"
                    value={newAssessment.dateCompleted}
                    onChange={(e) => setNewAssessment({...newAssessment, dateCompleted: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Results URL (optional)</label>
                  <input
                    type="url"
                    value={newAssessment.resultUrl}
                    onChange={(e) => setNewAssessment({...newAssessment, resultUrl: e.target.value})}
                    placeholder="https://example.com/results"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Results, Key Insights & Coaching Notes</label>
                <textarea
                  value={newAssessment.notes}
                  onChange={(e) => setNewAssessment({...newAssessment, notes: e.target.value})}
                  rows="5"
                  placeholder="Assessment results, key findings, insights, and action items"
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowNewAssessment(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Assessment Results</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAssignAssessment && (
        <div className="modal-overlay">
          <div className="modal">
            <h4>Assign Assessment to {selectedUser.firstName} {selectedUser.lastName}</h4>
            <form onSubmit={assignAssessmentToUser}>
              <div className="form-group">
                <label>Assessment Title *</label>
                <input
                  type="text"
                  value={assignAssessment.title}
                  onChange={(e) => setAssignAssessment({...assignAssessment, title: e.target.value})}
                  placeholder="e.g., StrengthsFinder 2.0"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={assignAssessment.category}
                    onChange={(e) => setAssignAssessment({...assignAssessment, category: e.target.value})}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Due Date (optional)</label>
                  <input
                    type="date"
                    value={assignAssessment.dueDate}
                    onChange={(e) => setAssignAssessment({...assignAssessment, dueDate: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={assignAssessment.description}
                  onChange={(e) => setAssignAssessment({...assignAssessment, description: e.target.value})}
                  rows="3"
                  placeholder="Brief description of what this assessment measures"
                />
              </div>

              <div className="form-group">
                <label>Instructions for Client</label>
                <textarea
                  value={assignAssessment.instructions}
                  onChange={(e) => setAssignAssessment({...assignAssessment, instructions: e.target.value})}
                  rows="4"
                  placeholder="Detailed instructions for how to complete this assessment"
                  required
                />
              </div>

              <div className="form-group">
                <label>Assessment URL</label>
                <input
                  type="url"
                  value={assignAssessment.assessmentUrl}
                  onChange={(e) => setAssignAssessment({...assignAssessment, assessmentUrl: e.target.value})}
                  placeholder="https://example.com/assessment"
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAssignAssessment(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Assign Assessment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editingAssessment && (
        <div className="modal-overlay">
          <div className="modal">
            <h4>Edit Assessment - {editingAssessment.customData?.title || editingAssessment.type}</h4>
            <form onSubmit={updateAssessmentAdmin}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={adminEditForm.title}
                  onChange={(e) => setAdminEditForm({...adminEditForm, title: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={adminEditForm.category}
                  onChange={(e) => setAdminEditForm({...adminEditForm, category: e.target.value})}
                  required
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={adminEditForm.description}
                  onChange={(e) => setAdminEditForm({...adminEditForm, description: e.target.value})}
                  rows="4"
                  placeholder="Brief description or summary"
                />
              </div>

              <div className="form-group">
                <label>Result/Report URL</label>
                <input
                  type="url"
                  value={adminEditForm.resultUrl}
                  onChange={(e) => setAdminEditForm({...adminEditForm, resultUrl: e.target.value})}
                  placeholder="https://example.com/results"
                />
              </div>

              <div className="form-group">
                <label>Notes/Key Takeaways</label>
                <textarea
                  value={adminEditForm.notes}
                  onChange={(e) => setAdminEditForm({...adminEditForm, notes: e.target.value})}
                  rows="4"
                  placeholder="Key findings, recommendations, or notes"
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={adminEditForm.status}
                  onChange={(e) => setAdminEditForm({...adminEditForm, status: e.target.value})}
                  required
                >
                  <option value="assigned">Assigned</option>
                  <option value="pending">Pending</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              {adminEditForm.status === 'completed' && (
                <div className="form-group">
                  <label>Date Completed</label>
                  <input
                    type="date"
                    value={adminEditForm.dateCompleted}
                    onChange={(e) => setAdminEditForm({...adminEditForm, dateCompleted: e.target.value})}
                  />
                </div>
              )}

              <div className="form-group admin-notes-form-group">
                <label>🔒 Admin Notes (Private - Admin Only)</label>
                <textarea
                  value={adminEditForm.adminNotes}
                  onChange={(e) => setAdminEditForm({...adminEditForm, adminNotes: e.target.value})}
                  rows="5"
                  placeholder="Add private notes visible only to admins..."
                  className="admin-notes-input"
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Update Assessment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="assessments-list">
        {assessments.map(assessment => (
          <div key={assessment._id} className="assessment-card">
            <div className="assessment-header">
              <div>
                <h4>{assessment.customData?.title || assessment.type.replace('-', ' ').toUpperCase()}</h4>
                <p className="assessment-category">{assessment.customData?.category || 'General'}</p>
              </div>
              <div className="admin-controls">
                <select
                  value={assessment.status}
                  onChange={(e) => quickStatusUpdate(assessment._id, e.target.value)}
                  className={`status-dropdown ${assessment.status}`}
                >
                  <option value="assigned">Assigned</option>
                  <option value="pending">Pending</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
                <button
                  onClick={() => openEditModal(assessment)}
                  className="btn btn-small edit-assessment-btn"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteAssessment(assessment._id)}
                  className="btn btn-small delete-btn"
                  title="Delete assessment"
                >
                  Delete
                </button>
              </div>
            </div>
            
            <div className="assessment-details">
              <p><strong>Assigned:</strong> {new Date(assessment.assignedDate).toLocaleDateString()}</p>
              {assessment.completedDate && (
                <p><strong>Completed:</strong> {new Date(assessment.completedDate).toLocaleDateString()}</p>
              )}
              {assessment.dueDate && (
                <p><strong>Due Date:</strong> {new Date(assessment.dueDate).toLocaleDateString()}</p>
              )}
              {assessment.customData?.assessmentUrl && (
                <p><strong>Assessment URL:</strong> 
                  <a href={assessment.customData.assessmentUrl} target="_blank" rel="noopener noreferrer" className="assessment-link">
                    {assessment.customData.assessmentUrl}
                  </a>
                </p>
              )}
            </div>

            {assessment.customData?.description && (
              <div className="assessment-description">
                <p><strong>Description:</strong> {assessment.customData.description}</p>
              </div>
            )}

            {assessment.customData?.instructions && (
              <div className="assessment-instructions">
                <p><strong>Instructions:</strong> {assessment.customData.instructions}</p>
              </div>
            )}

            {assessment.coachNotes && (
              <div className="coach-notes">
                <p><strong>Coach Notes:</strong> {assessment.coachNotes}</p>
              </div>
            )}

            {/* Admin Notes Section */}
            <div className="admin-notes-section">
              <div className="admin-notes-header">
                <h6>🔒 Admin Notes</h6>
                <span className="admin-only-badge">Admin Only</span>
              </div>

              {editingAssessmentAdminNote === assessment._id ? (
                <div className="admin-notes-edit">
                  <textarea
                    value={assessmentAdminNoteText}
                    onChange={(e) => setAssessmentAdminNoteText(e.target.value)}
                    placeholder="Add private admin notes about this assessment..."
                    className="admin-notes-textarea"
                    rows={4}
                  />
                  <div className="admin-notes-actions">
                    <button
                      onClick={() => handleSaveAssessmentAdminNote(assessment._id)}
                      className="save-admin-note-btn"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelAssessmentAdminNote}
                      className="cancel-admin-note-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="admin-notes-display">
                  {assessment.adminNotes ? (
                    <p>{assessment.adminNotes}</p>
                  ) : (
                    <p className="no-admin-notes">No admin notes yet</p>
                  )}
                  <button
                    onClick={() => handleEditAssessmentAdminNote(assessment._id, assessment.adminNotes || '')}
                    className="edit-admin-note-btn"
                  >
                    {assessment.adminNotes ? 'Edit Admin Notes' : 'Add Admin Notes'}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



// Progress Manager Component
function ProgressManager({ selectedUser, goals, progressNotes, onProgressUpdate, adminToken }) {
  const [refreshing, setRefreshing] = useState(false);
  const [editingAdminNote, setEditingAdminNote] = useState(null);
  const [adminNoteText, setAdminNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteData, setEditingNoteData] = useState({
    sessionTitle: '',
    sessionDate: '',
    notes: '',
    learnings: '',
    homework: []
  });
  const [showNewNoteModal, setShowNewNoteModal] = useState(false);
  const [newNoteData, setNewNoteData] = useState({
    sessionTitle: '',
    sessionDate: '',
    notes: '',
    learnings: '',
    homework: []
  });
  const [showNewGoalModal, setShowNewGoalModal] = useState(false);
  const [newGoalData, setNewGoalData] = useState({
    title: '',
    description: '',
    targetDate: '',
    status: 'not-started',
    priority: 'medium',
    category: 'General',
    progress: 0
  });
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editingGoalData, setEditingGoalData] = useState({
    title: '',
    description: '',
    targetDate: '',
    status: 'not-started',
    priority: 'medium',
    category: 'General',
    progress: 0
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await onProgressUpdate();
    // Small delay for visual feedback
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleEditAdminNote = (noteId, currentAdminNotes) => {
    setEditingAdminNote(noteId);
    setAdminNoteText(currentAdminNotes || '');
  };

  const handleCancelAdminNote = () => {
    setEditingAdminNote(null);
    setAdminNoteText('');
  };

  const handleSaveAdminNote = async (noteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/progress/notes/${noteId}/admin-notes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          adminNotes: adminNoteText,
          clientId: selectedUser._id
        })
      });

      if (response.ok) {
        setEditingAdminNote(null);
        setAdminNoteText('');
        await onProgressUpdate(); // Refresh the data
      } else {
        console.error('Failed to save admin note');
        alert('Failed to save admin note');
      }
    } catch (error) {
      console.error('Error saving admin note:', error);
      alert('Failed to save admin note');
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note._id);
    setEditingNoteData({
      sessionTitle: note.sessionTitle || '',
      sessionDate: note.sessionDate ? note.sessionDate.split('T')[0] : '',
      notes: note.notes || '',
      learnings: note.learnings || '',
      homework: Array.isArray(note.homework)
        ? note.homework.map(item => typeof item === 'object' ? item.text : item)
        : (note.homework ? [note.homework] : [])
    });
  };

  const handleSaveNote = async (noteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/progress/notes/${noteId}/admin`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          clientId: selectedUser._id,
          sessionTitle: editingNoteData.sessionTitle,
          sessionDate: editingNoteData.sessionDate,
          notes: editingNoteData.notes,
          learnings: editingNoteData.learnings,
          homework: editingNoteData.homework.filter(item => item.trim() !== '')
        })
      });

      if (response.ok) {
        setEditingNoteId(null);
        setEditingNoteData({
          sessionTitle: '',
          sessionDate: '',
          notes: '',
          learnings: '',
          homework: []
        });
        await onProgressUpdate();
      } else {
        console.error('Failed to update session note');
        alert('Failed to update session note');
      }
    } catch (error) {
      console.error('Error updating session note:', error);
      alert('Failed to update session note. Please try again.');
    }
  };

  const handleCancelNoteEdit = () => {
    setEditingNoteId(null);
    setEditingNoteData({
      sessionTitle: '',
      sessionDate: '',
      notes: '',
      learnings: '',
      homework: []
    });
  };

  const handleHomeworkChange = (index, value) => {
    const newHomework = [...editingNoteData.homework];
    newHomework[index] = value;
    setEditingNoteData({ ...editingNoteData, homework: newHomework });
  };

  const addHomeworkItem = () => {
    setEditingNoteData({
      ...editingNoteData,
      homework: [...editingNoteData.homework, '']
    });
  };

  const removeHomeworkItem = (index) => {
    const newHomework = editingNoteData.homework.filter((_, i) => i !== index);
    setEditingNoteData({ ...editingNoteData, homework: newHomework });
  };

  const handleCreateNewNote = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      alert('Please select a user first');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${selectedUser._id}/progress-notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          sessionTitle: newNoteData.sessionTitle,
          sessionDate: newNoteData.sessionDate,
          notes: newNoteData.notes,
          learnings: newNoteData.learnings,
          homework: newNoteData.homework.filter(item => item.trim() !== '')
        })
      });

      if (response.ok) {
        setShowNewNoteModal(false);
        setNewNoteData({
          sessionTitle: '',
          sessionDate: '',
          notes: '',
          learnings: '',
          homework: []
        });
        await onProgressUpdate();
        alert('Session note created successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to create session note: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error creating session note:', error);
      alert('Failed to create session note. Please try again.');
    }
  };

  const handleNewNoteHomeworkChange = (index, value) => {
    const newHomework = [...newNoteData.homework];
    newHomework[index] = value;
    setNewNoteData({ ...newNoteData, homework: newHomework });
  };

  const addNewNoteHomeworkItem = () => {
    setNewNoteData({
      ...newNoteData,
      homework: [...newNoteData.homework, '']
    });
  };

  const removeNewNoteHomeworkItem = (index) => {
    const newHomework = newNoteData.homework.filter((_, i) => i !== index);
    setNewNoteData({ ...newNoteData, homework: newHomework });
  };

  const handleCreateNewGoal = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      alert('Please select a user first');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${selectedUser._id}/goals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          title: newGoalData.title,
          description: newGoalData.description,
          targetDate: newGoalData.targetDate,
          status: newGoalData.status,
          priority: newGoalData.priority,
          category: newGoalData.category,
          progress: newGoalData.progress
        })
      });

      if (response.ok) {
        setShowNewGoalModal(false);
        setNewGoalData({
          title: '',
          description: '',
          targetDate: '',
          status: 'not-started',
          priority: 'medium',
          category: 'General',
          progress: 0
        });
        await onProgressUpdate();
        alert('Goal created successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to create goal: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error creating goal:', error);
      alert('Failed to create goal. Please try again.');
    }
  };

  const handleEditGoal = (goal) => {
    setEditingGoalId(goal._id);
    setEditingGoalData({
      title: goal.title,
      description: goal.description,
      targetDate: goal.targetDate ? goal.targetDate.split('T')[0] : '',
      status: goal.status,
      priority: goal.priority || 'medium',
      category: goal.category || 'General',
      progress: goal.progress || 0
    });
  };

  const handleSaveGoal = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${selectedUser._id}/goals/${editingGoalId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(editingGoalData)
      });

      if (response.ok) {
        setEditingGoalId(null);
        setEditingGoalData({ title: '', description: '', targetDate: '', status: 'not-started', priority: 'medium', category: 'General', progress: 0 });
        await onProgressUpdate();
      } else {
        const errorData = await response.json();
        alert(`Failed to update goal: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error updating goal:', error);
      alert('Failed to update goal. Please try again.');
    }
  };

  const handleCancelGoalEdit = () => {
    setEditingGoalId(null);
    setEditingGoalData({ title: '', description: '', targetDate: '', status: 'not-started', priority: 'medium', category: 'General', progress: 0 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#28a745';
      case 'in-progress': return '#007bff';
      case 'not-started': return '#6c757d';
      case 'on-hold': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const handleDownloadNotes = () => {
    const clientName = `${selectedUser.firstName} ${selectedUser.lastName}`;
    const lines = [
      `Session Notes — ${clientName}`,
      `Downloaded: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
      '='.repeat(60),
      ''
    ];

    if (progressNotes.length === 0) {
      lines.push('No session notes found.');
    } else {
      progressNotes.forEach((note, i) => {
        lines.push(`Session ${i + 1}: ${note.sessionTitle || '(Untitled)'}`);
        lines.push(`Date: ${formatDate(note.sessionDate)}`);
        lines.push(`Created by: ${note.createdBy === 'coach' ? 'Coach' : 'Client'}`);
        lines.push('-'.repeat(40));

        if (note.notes) {
          lines.push('Client Notes:');
          lines.push(note.notes);
          lines.push('');
        }

        if (note.learnings) {
          lines.push('Key Learnings:');
          lines.push(note.learnings);
          lines.push('');
        }

        if (note.homework && note.homework.length > 0) {
          lines.push('Homework/Experiments:');
          note.homework.forEach(item => {
            const status = item.completed ? '[x]' : '[ ]';
            lines.push(`  ${status} ${item.text}`);
          });
          lines.push('');
        }

        if (note.adminNotes) {
          lines.push('Admin Notes (Private):');
          lines.push(note.adminNotes);
          if (note.adminNotesUpdatedAt) {
            lines.push(`  (Updated ${new Date(note.adminNotesUpdatedAt).toLocaleDateString()}${note.adminNotesUpdatedBy ? ` by ${note.adminNotesUpdatedBy}` : ''})`);
          }
          lines.push('');
        }

        lines.push('');
      });
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${clientName.replace(/\s+/g, '_')}_session_notes.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!selectedUser) {
    return (
      <div className="no-selection">
        <p>Select a user from the Users tab to view their progress tracking data</p>
      </div>
    );
  }

  return (
    <div className="progress-manager">
      <div className="section-header">
        <h3>Session Prep and Notes for {selectedUser.firstName} {selectedUser.lastName}</h3>
        <div className="section-actions">
          <button
            onClick={handleRefresh}
            className={`btn btn-outline refresh-btn ${refreshing ? 'refreshing' : ''}`}
            title="Refresh progress data"
            disabled={refreshing}
          >
            {refreshing ? '⟳ Refreshing...' : '↻ Refresh'}
          </button>
          {progressNotes.length > 0 && (
            <button
              onClick={handleDownloadNotes}
              className="btn btn-outline"
              title="Download all session notes as a text file"
            >
              ↓ Download Notes
            </button>
          )}
          <button
            onClick={() => setShowNewNoteModal(true)}
            className="btn btn-primary"
          >
            + Start New Session Note
          </button>
        </div>
      </div>

      {/* Goals Section */}
      <div className="progress-section">
        <div className="section-header-inline">
          <h4>Goals ({goals.length})</h4>
          <button
            onClick={() => setShowNewGoalModal(true)}
            className="btn btn-primary btn-small"
          >
            + Add Goal
          </button>
        </div>
        {goals.length === 0 ? (
          <div className="empty-state">
            <p>No goals found for this user.</p>
          </div>
        ) : (
          <div className="goals-list">
            {goals.map(goal => (
              <div key={goal._id} className="goal-item">
                <div className="goal-header">
                  <h5>{goal.title}</h5>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(goal.status) }}
                    >
                      {goal.status.replace('-', ' ')}
                    </span>
                    <button
                      onClick={() => handleEditGoal(goal)}
                      className="btn btn-secondary btn-small"
                    >
                      Edit
                    </button>
                  </div>
                </div>
                <p className="goal-description">{goal.description}</p>
                <div className="goal-details">
                  <div className="progress-bar">
                    <label>Progress: {goal.progress}%</label>
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{ width: `${goal.progress}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="goal-meta">
                    <span><strong>Target Date:</strong> {formatDate(goal.targetDate)}</span>
                    <span><strong>Created:</strong> {formatDate(goal.createdAt)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Goal Modal */}
      {showNewGoalModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h4>Add New Goal for {selectedUser.firstName} {selectedUser.lastName}</h4>
            <form onSubmit={handleCreateNewGoal}>
              <div className="form-group">
                <label>Goal Title *</label>
                <input
                  type="text"
                  value={newGoalData.title}
                  onChange={(e) => setNewGoalData({ ...newGoalData, title: e.target.value })}
                  placeholder="e.g., Improve leadership skills"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={newGoalData.description}
                  onChange={(e) => setNewGoalData({ ...newGoalData, description: e.target.value })}
                  rows="4"
                  placeholder="Detailed description of the goal and key results..."
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Target Date *</label>
                  <input
                    type="date"
                    value={newGoalData.targetDate}
                    onChange={(e) => setNewGoalData({ ...newGoalData, targetDate: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={newGoalData.status}
                    onChange={(e) => setNewGoalData({ ...newGoalData, status: e.target.value })}
                  >
                    <option value="not-started">Not Started</option>
                    <option value="in-progress">In Progress</option>
                    <option value="on-hold">On Hold</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={newGoalData.priority}
                    onChange={(e) => setNewGoalData({ ...newGoalData, priority: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={newGoalData.category}
                    onChange={(e) => setNewGoalData({ ...newGoalData, category: e.target.value })}
                    placeholder="e.g., Leadership, Career, Personal"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Initial Progress (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={newGoalData.progress}
                  onChange={(e) => setNewGoalData({ ...newGoalData, progress: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => {
                  setShowNewGoalModal(false);
                  setNewGoalData({
                    title: '',
                    description: '',
                    targetDate: '',
                    status: 'not-started',
                    priority: 'medium',
                    category: 'General',
                    progress: 0
                  });
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Create Goal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Goal Modal */}
      {editingGoalId && (
        <div className="modal-overlay">
          <div className="modal">
            <h4>Edit Goal for {selectedUser.firstName} {selectedUser.lastName}</h4>
            <form onSubmit={handleSaveGoal}>
              <div className="form-group">
                <label>Goal Title *</label>
                <input
                  type="text"
                  value={editingGoalData.title}
                  onChange={(e) => setEditingGoalData({ ...editingGoalData, title: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={editingGoalData.description}
                  onChange={(e) => setEditingGoalData({ ...editingGoalData, description: e.target.value })}
                  rows="4"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Target Date *</label>
                  <input
                    type="date"
                    value={editingGoalData.targetDate}
                    onChange={(e) => setEditingGoalData({ ...editingGoalData, targetDate: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={editingGoalData.status}
                    onChange={(e) => setEditingGoalData({ ...editingGoalData, status: e.target.value })}
                  >
                    <option value="not-started">Not Started</option>
                    <option value="in-progress">In Progress</option>
                    <option value="on-hold">On Hold</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={editingGoalData.priority}
                    onChange={(e) => setEditingGoalData({ ...editingGoalData, priority: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={editingGoalData.category}
                    onChange={(e) => setEditingGoalData({ ...editingGoalData, category: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Progress (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={editingGoalData.progress}
                  onChange={(e) => setEditingGoalData({ ...editingGoalData, progress: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={handleCancelGoalEdit}>Cancel</button>
                <button type="submit" className="btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Session Note Modal */}
      {showNewNoteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h4>Start New Session Note for {selectedUser.firstName} {selectedUser.lastName}</h4>
            <form onSubmit={handleCreateNewNote}>
              <div className="form-group">
                <label>Session Date *</label>
                <input
                  type="date"
                  value={newNoteData.sessionDate}
                  onChange={(e) => setNewNoteData({ ...newNoteData, sessionDate: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Session Title *</label>
                <input
                  type="text"
                  value={newNoteData.sessionTitle}
                  onChange={(e) => setNewNoteData({ ...newNoteData, sessionTitle: e.target.value })}
                  placeholder="e.g., Weekly Coaching Session, Goal Review, etc."
                  required
                />
              </div>

              <div className="form-group">
                <label>Session Notes</label>
                <textarea
                  value={newNoteData.notes}
                  onChange={(e) => setNewNoteData({ ...newNoteData, notes: e.target.value })}
                  rows="4"
                  placeholder="Notes from the session..."
                />
              </div>

              <div className="form-group">
                <label>Key Learnings</label>
                <textarea
                  value={newNoteData.learnings}
                  onChange={(e) => setNewNoteData({ ...newNoteData, learnings: e.target.value })}
                  rows="4"
                  placeholder="Key insights and learnings from the session..."
                />
              </div>

              <div className="form-group">
                <label>Homework/Experiments</label>
                {newNoteData.homework.length === 0 ? (
                  <button
                    type="button"
                    onClick={addNewNoteHomeworkItem}
                    className="add-homework-btn"
                  >
                    + Add Homework Item
                  </button>
                ) : (
                  <>
                    {newNoteData.homework.map((item, index) => (
                      <div key={index} className="homework-input-group">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => handleNewNoteHomeworkChange(index, e.target.value)}
                          placeholder={`Homework item ${index + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeNewNoteHomeworkItem(index)}
                          className="remove-homework-btn"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addNewNoteHomeworkItem}
                      className="add-homework-btn"
                    >
                      + Add Another Homework Item
                    </button>
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => {
                  setShowNewNoteModal(false);
                  setNewNoteData({
                    sessionTitle: '',
                    sessionDate: '',
                    notes: '',
                    learnings: '',
                    homework: []
                  });
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Create Session Note</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Progress Notes Section */}
      <div className="progress-section">
        <h4>Session Notes ({progressNotes.length})</h4>
        {progressNotes.length === 0 ? (
          <div className="empty-state">
            <p>No session notes found for this user.</p>
          </div>
        ) : (
          <div className="notes-list">
            {progressNotes.map(note => (
              <div key={note._id} className="note-item">
                {editingNoteId === note._id ? (
                  // Edit Mode
                  <div className="note-edit-mode">
                    <div className="note-header">
                      <h5>Edit Session Note</h5>
                      <div className="edit-actions">
                        <button onClick={() => handleSaveNote(note._id)} className="btn btn-primary btn-small">
                          Save
                        </button>
                        <button onClick={handleCancelNoteEdit} className="btn btn-outline btn-small">
                          Cancel
                        </button>
                      </div>
                    </div>

                    <div className="note-edit-form">
                      <div className="form-group">
                        <label>Session Title *</label>
                        <input
                          type="text"
                          value={editingNoteData.sessionTitle}
                          onChange={(e) => setEditingNoteData({ ...editingNoteData, sessionTitle: e.target.value })}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Session Date *</label>
                        <input
                          type="date"
                          value={editingNoteData.sessionDate}
                          onChange={(e) => setEditingNoteData({ ...editingNoteData, sessionDate: e.target.value })}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Session Notes *</label>
                        <textarea
                          value={editingNoteData.notes}
                          onChange={(e) => setEditingNoteData({ ...editingNoteData, notes: e.target.value })}
                          rows="4"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Key Learnings</label>
                        <textarea
                          value={editingNoteData.learnings}
                          onChange={(e) => setEditingNoteData({ ...editingNoteData, learnings: e.target.value })}
                          rows="4"
                        />
                      </div>

                      <div className="form-group">
                        <label>Homework/Experiments</label>
                        {editingNoteData.homework.map((item, index) => (
                          <div key={index} className="homework-input-group">
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => handleHomeworkChange(index, e.target.value)}
                              placeholder={`Homework item ${index + 1}`}
                            />
                            <button
                              type="button"
                              onClick={() => removeHomeworkItem(index)}
                              className="remove-homework-btn"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addHomeworkItem}
                          className="add-homework-btn"
                        >
                          + Add Homework Item
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Display Mode
                  <>
                    <div className="note-header">
                      <div>
                        <h5>{note.sessionTitle}</h5>
                        <span className="session-date">{formatDate(note.sessionDate)}</span>
                      </div>
                      <div className="note-header-actions">
                        <button
                          onClick={() => handleEditNote(note)}
                          className="btn btn-small edit-note-btn"
                        >
                          Edit
                        </button>
                        <span className={`homework-status ${note.completed ? 'completed' : 'pending'}`}>
                          Homework: {note.completed ? 'Completed' : 'Pending'}
                        </span>
                      </div>
                    </div>

                    <div className="note-content">
                      <div className="note-section">
                        <h6>Session Notes:</h6>
                        <p>{note.notes}</p>
                      </div>

                      <div className="note-section">
                        <h6>Key Learnings:</h6>
                        <p>{note.learnings}</p>
                      </div>

                      <div className="note-section">
                        <h6>Homework/Experiments:</h6>
                        {Array.isArray(note.homework) ? (
                          <div className="homework-list">
                            {note.homework.map((item, index) => {
                              const homeworkItem = typeof item === 'object' ? item : { text: item, completed: false };
                              return (
                                <div key={index} className="homework-item-display">
                                  <div className="homework-item-admin">
                                    <span className={homeworkItem.completed ? 'completed-item' : ''}>
                                      {homeworkItem.completed ? '✓' : '○'} {homeworkItem.text}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p>{note.homework}</p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Admin Notes Section - Always visible */}
                {editingNoteId !== note._id && (
                  <div className="note-section admin-notes-section">
                    <div className="admin-notes-header">
                      <h6>🔒 Admin Notes</h6>
                      <span className="admin-only-badge">Admin Only</span>
                    </div>

                    {editingAdminNote === note._id ? (
                      <div className="admin-notes-edit">
                        <textarea
                          value={adminNoteText}
                          onChange={(e) => setAdminNoteText(e.target.value)}
                          placeholder="Add private admin notes about this session..."
                          className="admin-notes-textarea"
                          rows={4}
                        />
                        <div className="admin-notes-actions">
                          <button
                            onClick={() => handleSaveAdminNote(note._id)}
                            className="save-admin-note-btn"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelAdminNote}
                            className="cancel-admin-note-btn"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="admin-notes-display">
                        {note.adminNotes ? (
                          <>
                            <p>{note.adminNotes}</p>
                            {note.adminNotesUpdatedAt && (
                              <div className="admin-notes-meta">
                                <small>
                                  Updated {new Date(note.adminNotesUpdatedAt).toLocaleDateString()}
                                  {note.adminNotesUpdatedBy && ` by ${note.adminNotesUpdatedBy}`}
                                </small>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="no-admin-notes">No admin notes yet</p>
                        )}
                        <button
                          onClick={() => handleEditAdminNote(note._id, note.adminNotes || '')}
                          className="edit-admin-note-btn"
                        >
                          {note.adminNotes ? 'Edit Admin Notes' : 'Add Admin Notes'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// Intake Data Viewer Component
function IntakeViewer({ selectedUser, intakeData }) {
  if (!selectedUser) {
    return (
      <div className="no-selection">
        <p>Select a user from the Users tab to view their intake form responses</p>
      </div>
    );
  }

  if (!intakeData) {
    return (
      <div className="intake-viewer">
        <div className="section-header">
          <h3>Intake Form Data for {selectedUser.firstName} {selectedUser.lastName}</h3>
        </div>
        <div className="no-data">
          <p>This user has not completed the intake form yet.</p>
        </div>
      </div>
    );
  }

  const formatAssessmentStatus = (status) => {
    const statusMap = {
      'never': 'Never taken',
      'recent': 'Taken in last 18 months',
      'old': 'Taken longer than 18 months ago',
      'unsure': 'Unsure'
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  return (
    <div className="intake-viewer">
      <div className="section-header">
        <h3>Intake Form Data for {selectedUser.firstName} {selectedUser.lastName}</h3>
        <div className="intake-meta">
          <span>Submitted: {formatDate(intakeData.submittedAt)}</span>
          {intakeData.lastUpdated && intakeData.lastUpdated !== intakeData.submittedAt && (
            <span>Last Updated: {formatDate(intakeData.lastUpdated)}</span>
          )}
        </div>
      </div>

      <div className="intake-sections">
        {/* Personal Information */}
        <div className="intake-section">
          <h4>Personal Information</h4>
          <div className="intake-content">
            <div className="form-row">
              <div className="field-group">
                <label>Preferred Name:</label>
                <span>{intakeData.preferredName || 'Not provided'}</span>
              </div>
              <div className="field-group">
                <label>Last Name:</label>
                <span>{intakeData.lastName || 'Not provided'}</span>
              </div>
            </div>
            <div className="form-row">
              <div className="field-group">
                <label>Phone:</label>
                <span>{intakeData.phone || 'Not provided'}</span>
              </div>
              <div className="field-group">
                <label>Date of Birth:</label>
                <span>{formatDate(intakeData.dateOfBirth)}</span>
              </div>
            </div>
            
            <div className="field-group">
              <label>Mailing Address:</label>
              <div className="address-block">
                <div>{intakeData.address || 'Not provided'}</div>
                {(intakeData.city || intakeData.state || intakeData.zipCode) && (
                  <div>
                    {[intakeData.city, intakeData.state, intakeData.zipCode].filter(Boolean).join(', ')}
                  </div>
                )}
                {intakeData.country && intakeData.country !== 'United States' && (
                  <div>{intakeData.country}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Personal Achievements */}
        <div className="intake-section">
          <h4>Personal Achievements</h4>
          <div className="intake-content">
            <div className="field-group">
              <label>Greatest Achievement:</label>
              <p>{intakeData.greatestAchievement || 'Not provided'}</p>
            </div>
            <div className="field-group">
              <label>Next Major Milestone:</label>
              <p>{intakeData.nextMilestone || 'Not provided'}</p>
            </div>
          </div>
        </div>

        {/* Self-Assessments */}
        <div className="intake-section">
          <h4>Self-Assessments</h4>
          <div className="intake-content">
            <div className="assessments-grid">
              {intakeData.assessments && Object.entries(intakeData.assessments).map(([key, status]) => {
                const labels = {
                  'insightsDiscovery': 'Insights Discovery',
                  'cliftonStrengths': 'Clifton Strengths',
                  'myersBriggs': 'Myers-Briggs',
                  'leadershipCircle': 'Leadership Circle Profile',
                  'discProfile': 'DISC Profile',
                  'other': 'Other'
                };
                
                return (
                  <div key={key} className="assessment-item">
                    <div className="assessment-name">
                      {key === 'other' && intakeData.otherAssessmentName 
                        ? intakeData.otherAssessmentName 
                        : labels[key] || key
                      }
                    </div>
                    <div className="assessment-status">{formatAssessmentStatus(status)}</div>
                    {intakeData.assessmentResults && intakeData.assessmentResults[key] && status !== 'never' && (
                      <div className="assessment-results">
                        <label>Results/Details:</label>
                        <p>{intakeData.assessmentResults[key]}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Coaching Goals */}
        <div className="intake-section">
          <h4>Coaching Goals</h4>
          <div className="intake-content">
            <div className="field-group">
              <label>Previous Coaching Experience:</label>
              <span>{intakeData.previousCoaching ? 'Yes' : 'No'}</span>
              {intakeData.previousCoaching && intakeData.previousCoachingDescription && (
                <div className="sub-field">
                  <label>Previous Experience Details:</label>
                  <p>{intakeData.previousCoachingDescription}</p>
                </div>
              )}
            </div>
            <div className="field-group">
              <label>Why Seeking a Coach:</label>
              <p>{intakeData.whySeekingCoach || 'Not provided'}</p>
            </div>
            <div className="field-group">
              <label>Specific Goals:</label>
              <p>{intakeData.specificGoals || 'Not provided'}</p>
            </div>
            <div className="field-group">
              <label>Current Challenges:</label>
              <p>{intakeData.currentChallenges || 'Not provided'}</p>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="intake-section">
          <h4>Additional Information</h4>
          <div className="intake-content">
            <div className="field-group">
              <label>Additional Info:</label>
              <p>{intakeData.additionalInfo || 'Not provided'}</p>
            </div>
            <div className="field-group">
              <label>Coach Expectations:</label>
              <p>{intakeData.coachExpectations || 'Not provided'}</p>
            </div>
            <div className="field-group">
              <label>Hobbies & Interests:</label>
              <p>{intakeData.hobbies || 'Not provided'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Contract Manager Component
function ContractManager({ selectedUser, contracts, onContractUpdate, adminToken }) {
  const [showNewContract, setShowNewContract] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [htmlAgreement, setHtmlAgreement] = useState(null);
  const [htmlAgreementLoading, setHtmlAgreementLoading] = useState(false);
  const [showHtmlModal, setShowHtmlModal] = useState(false);

  useEffect(() => {
    if (!selectedUser) return;
    setHtmlAgreement(null);
    setHtmlAgreementLoading(true);
    fetch(`${API_BASE_URL}/api/admin/users/${selectedUser._id}/coaching-agreement`, {
      headers: { Authorization: `Bearer ${adminToken}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => setHtmlAgreement(data))
      .catch(() => setHtmlAgreement(null))
      .finally(() => setHtmlAgreementLoading(false));
  }, [selectedUser, adminToken]);
  const [newContract, setNewContract] = useState({
    title: '',
    contractType: 'coaching-agreement',
    description: '',
    status: 'draft',
    expiryDate: '',
    notes: '',
    adminNotes: '',
    contractTerms: {
      duration: '',
      startDate: '',
      endDate: '',
      sessionFrequency: '',
      totalSessions: '',
      rate: {
        amount: '',
        currency: 'USD',
        period: 'per session'
      }
    }
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const contractTypes = [
    { value: 'coaching-agreement', label: 'Coaching Agreement' },
    { value: 'service-contract', label: 'Service Contract' },
    { value: 'nda', label: 'Non-Disclosure Agreement' },
    { value: 'other', label: 'Other' }
  ];

  const contractStatuses = [
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent to Client' },
    { value: 'signed', label: 'Signed' },
    { value: 'expired', label: 'Expired' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const createContract = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      alert('Please select a user first');
      return;
    }

    try {
      console.log('Admin creating contract for user:', selectedUser);
      
      const response = await fetch(API_ENDPOINTS.ADMIN_CONTRACTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          client: selectedUser._id,
          ...newContract
        })
      });

      if (response.ok) {
        const createdContract = await response.json();

        // Upload file if one was selected
        if (selectedFile && createdContract._id) {
          await uploadContractFile(createdContract._id, selectedFile);
        }

        setShowNewContract(false);
        setSelectedFile(null);
        setNewContract({
          title: '',
          contractType: 'coaching-agreement',
          description: '',
          status: 'draft',
          expiryDate: '',
          notes: '',
          adminNotes: '',
          contractTerms: {
            duration: '',
            startDate: '',
            endDate: '',
            sessionFrequency: '',
            totalSessions: '',
            rate: {
              amount: '',
              currency: 'USD',
              period: 'per session'
            }
          }
        });
        onContractUpdate();
        alert('Contract created successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to create contract: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error creating contract:', error);
      alert('Failed to create contract. Please try again.');
    }
  };

  const openEditModal = (contract) => {
    setEditingContract(contract);
    setShowEditModal(true);
  };

  const updateContractStatus = async (contractId, newStatus) => {
    try {
      const response = await fetch(buildApiUrl('/api/admin/contracts/:contractId/status', { contractId }), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ 
          status: newStatus,
          signedDate: newStatus === 'signed' ? new Date().toISOString() : null
        })
      });

      if (response.ok) {
        onContractUpdate();
      } else {
        console.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await onContractUpdate();
    setTimeout(() => setRefreshing(false), 500);
  };

  const uploadContractFile = async (contractId, file) => {
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(buildApiUrl('/api/admin/contracts/:contractId/upload', { contractId }), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        body: formData
      });

      if (response.ok) {
        onContractUpdate();
      } else {
        const errorData = await response.json();
        alert(`Failed to upload file: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error uploading contract file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFile(false);
    }
  };

  const viewContractPdf = async (contractId, fileName) => {
    setLoadingPdf(true);
    try {
      const response = await fetch(buildApiUrl('/api/admin/contracts/:contractId/file', { contractId }), {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setViewingPdf({ url: data.fileUrl, fileName: data.fileName });
      } else {
        const errorData = await response.json();
        alert(`Failed to load PDF: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error loading contract PDF:', error);
      alert('Failed to load PDF. Please try again.');
    } finally {
      setLoadingPdf(false);
    }
  };

  const deleteContractFile = async (contractId) => {
    if (!window.confirm('Are you sure you want to remove this file? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl('/api/admin/contracts/:contractId/file', { contractId }), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        onContractUpdate();
      } else {
        const errorData = await response.json();
        alert(`Failed to delete file: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error deleting contract file:', error);
      alert('Failed to delete file. Please try again.');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (file.type !== 'application/pdf') {
      alert('Only PDF files are allowed.');
      e.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be under 10MB.');
      e.target.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'signed': return '#28a745';
      case 'sent': return '#007bff';
      case 'draft': return '#6c757d';
      case 'expired': return '#dc3545';
      case 'cancelled': return '#ffc107';
      default: return '#6c757d';
    }
  };

  if (!selectedUser) {
    return (
      <div className="no-selection">
        <p>Select a user from the Users tab to manage their contracts</p>
      </div>
    );
  }

  return (
    <div className="contract-manager">
      <div className="section-header">
        <h3>Contracts for {selectedUser.firstName} {selectedUser.lastName}</h3>
        <div className="section-actions">
          <button 
            onClick={handleRefresh}
            className={`btn btn-outline refresh-btn ${refreshing ? 'refreshing' : ''}`}
            title="Refresh contracts"
            disabled={refreshing}
          >
            {refreshing ? '⟳ Refreshing...' : '↻ Refresh'}
          </button>
          <button 
            onClick={() => setShowNewContract(true)}
            className="btn btn-primary"
          >
            New Contract
          </button>
        </div>
      </div>

      {showNewContract && (
        <div className="modal-overlay">
          <div className="modal">
            <h4>Create New Contract</h4>
            <form onSubmit={createContract}>
              <div className="form-row">
                <div className="form-group">
                  <label>Contract Title</label>
                  <input
                    type="text"
                    value={newContract.title}
                    onChange={(e) => setNewContract({...newContract, title: e.target.value})}
                    placeholder="e.g., Leadership Coaching Agreement"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Contract Type</label>
                  <select
                    value={newContract.contractType}
                    onChange={(e) => setNewContract({...newContract, contractType: e.target.value})}
                  >
                    {contractTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newContract.description}
                  onChange={(e) => setNewContract({...newContract, description: e.target.value})}
                  rows="3"
                  placeholder="Brief description of the contract"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={newContract.status}
                    onChange={(e) => setNewContract({...newContract, status: e.target.value})}
                  >
                    {contractStatuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Expiry Date (optional)</label>
                  <input
                    type="date"
                    value={newContract.expiryDate}
                    onChange={(e) => setNewContract({...newContract, expiryDate: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-section">
                <h5>Contract Terms</h5>
                <div className="form-row">
                  <div className="form-group">
                    <label>Duration</label>
                    <input
                      type="text"
                      value={newContract.contractTerms.duration}
                      onChange={(e) => setNewContract({
                        ...newContract, 
                        contractTerms: {...newContract.contractTerms, duration: e.target.value}
                      })}
                      placeholder="e.g., 6 months, 1 year"
                    />
                  </div>
                  <div className="form-group">
                    <label>Session Frequency</label>
                    <input
                      type="text"
                      value={newContract.contractTerms.sessionFrequency}
                      onChange={(e) => setNewContract({
                        ...newContract, 
                        contractTerms: {...newContract.contractTerms, sessionFrequency: e.target.value}
                      })}
                      placeholder="e.g., weekly, bi-weekly"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={newContract.contractTerms.startDate}
                      onChange={(e) => setNewContract({
                        ...newContract, 
                        contractTerms: {...newContract.contractTerms, startDate: e.target.value}
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={newContract.contractTerms.endDate}
                      onChange={(e) => setNewContract({
                        ...newContract, 
                        contractTerms: {...newContract.contractTerms, endDate: e.target.value}
                      })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Total Sessions</label>
                    <input
                      type="number"
                      value={newContract.contractTerms.totalSessions}
                      onChange={(e) => setNewContract({
                        ...newContract, 
                        contractTerms: {...newContract.contractTerms, totalSessions: e.target.value}
                      })}
                      placeholder="e.g., 12"
                    />
                  </div>
                  <div className="form-group">
                    <label>Rate Amount</label>
                    <input
                      type="number"
                      value={newContract.contractTerms.rate.amount}
                      onChange={(e) => setNewContract({
                        ...newContract, 
                        contractTerms: {
                          ...newContract.contractTerms, 
                          rate: {...newContract.contractTerms.rate, amount: e.target.value}
                        }
                      })}
                      placeholder="e.g., 150"
                    />
                  </div>
                  <div className="form-group">
                    <label>Rate Period</label>
                    <select
                      value={newContract.contractTerms.rate.period}
                      onChange={(e) => setNewContract({
                        ...newContract, 
                        contractTerms: {
                          ...newContract.contractTerms, 
                          rate: {...newContract.contractTerms.rate, period: e.target.value}
                        }
                      })}
                    >
                      <option value="per session">Per Session</option>
                      <option value="per month">Per Month</option>
                      <option value="total">Total</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newContract.notes}
                  onChange={(e) => setNewContract({...newContract, notes: e.target.value})}
                  rows="3"
                  placeholder="Additional notes about this contract"
                />
              </div>

              <div className="form-group">
                <label>Admin Notes (Private)</label>
                <textarea
                  value={newContract.adminNotes}
                  onChange={(e) => setNewContract({...newContract, adminNotes: e.target.value})}
                  rows="2"
                  placeholder="Private notes visible only to admins"
                />
              </div>

              <div className="form-group">
                <label>Contract PDF (optional)</label>
                <div className="file-input">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                  />
                  <p className="file-input-hint">PDF files only, max 10MB</p>
                </div>
                {selectedFile && (
                  <div className="file-selected">
                    <span>{selectedFile.name}</span>
                    <span className="file-size">({formatFileSize(selectedFile.size)})</span>
                    <button type="button" className="btn-remove-file" onClick={() => setSelectedFile(null)}>Remove</button>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => { setShowNewContract(false); setSelectedFile(null); }}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={uploadingFile}>
                  {uploadingFile ? 'Uploading...' : 'Create Contract'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewingPdf && (
        <div className="modal-overlay pdf-viewer-modal">
          <div className="pdf-viewer-container">
            <div className="pdf-viewer-header">
              <h4>{viewingPdf.fileName}</h4>
              <button className="close-btn" onClick={() => setViewingPdf(null)}>&times;</button>
            </div>
            <div className="pdf-viewer-content">
              <iframe
                src={viewingPdf.url}
                className="pdf-iframe"
                title={viewingPdf.fileName}
              />
            </div>
            <div className="pdf-viewer-footer">
              <a
                href={viewingPdf.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline"
              >
                Open in New Tab
              </a>
              <button className="btn btn-primary" onClick={() => setViewingPdf(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* HTML Coaching Agreement modal */}
      {showHtmlModal && htmlAgreement && (
        <div className="modal-overlay" onClick={() => setShowHtmlModal(false)}>
          <div className="modal modal-large agreement-view-modal" onClick={e => e.stopPropagation()}>
            <div className="agreement-modal-header">
              <h4>Coaching Agreement — {selectedUser.firstName} {selectedUser.lastName}</h4>
              <button className="close-btn" onClick={() => setShowHtmlModal(false)}>&times;</button>
            </div>
            <div className="agreement-modal-body">
              <div dangerouslySetInnerHTML={{ __html: htmlAgreement.html }} />
            </div>
            <div className="agreement-signature-block">
              <div className="agreement-sig-party">
                <p className="agreement-sig-label">NextLeader Coaching (Coach)</p>
                <p className="agreement-sig-name">Irwin Tran — Owner</p>
                <p className="agreement-sig-signed">✓ Signed{htmlAgreement.coachSignedDate ? ` ${new Date(htmlAgreement.coachSignedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}` : ''}</p>
              </div>
              <div className="agreement-sig-party">
                <p className="agreement-sig-label">Client</p>
                {htmlAgreement.signed ? (
                  <>
                    <p className="agreement-sig-name">{htmlAgreement.signedName}</p>
                    <p className="agreement-sig-signed">✓ Signed {new Date(htmlAgreement.signedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </>
                ) : (
                  <p className="agreement-sig-pending">Pending signature</p>
                )}
              </div>
            </div>
            <div className="modal-actions" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', justifyContent: 'space-between' }}>
              <button
                className="btn-download-pdf"
                onClick={() => printAgreementPdf(htmlAgreement, `${selectedUser.firstName} ${selectedUser.lastName}`)}
              >
                ↓ Download PDF
              </button>
              <button className="btn-primary" onClick={() => setShowHtmlModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* HTML Coaching Agreement card */}
      {(htmlAgreementLoading || htmlAgreement) && (
        <div className="coaching-agreement-card">
          <div className="coaching-agreement-card-header">
            <div>
              <h4>Coaching Partnership Agreement</h4>
              <p className="contract-type">Coaching Agreement</p>
            </div>
            <div className="contract-controls">
              {htmlAgreement?.signed ? (
                <span className="status-badge completed">Signed</span>
              ) : (
                <span className="status-badge pending">Pending Signature</span>
              )}
              <button
                className="btn btn-outline"
                onClick={() => setShowHtmlModal(true)}
                disabled={htmlAgreementLoading || !htmlAgreement}
              >
                {htmlAgreementLoading ? 'Loading...' : 'View Agreement'}
              </button>
            </div>
          </div>
          {htmlAgreement?.signed && (
            <p className="contract-signed-info">
              Signed by {htmlAgreement.signedName} on {new Date(htmlAgreement.signedDate).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      <div className="contracts-list">
        {contracts.length === 0 ? (
          !htmlAgreement && !htmlAgreementLoading ? (
            <div className="empty-state">
              <p>No contracts found for this user.</p>
            </div>
          ) : null
        ) : (
          contracts.map(contract => (
            <div key={contract._id} className="contract-card" data-status={contract.status}>
              <div className="contract-header">
                <div>
                  <h4>{contract.title}</h4>
                  <p className="contract-type">{contractTypes.find(t => t.value === contract.contractType)?.label}</p>
                </div>
                <div className="contract-controls">
                  <select
                    value={contract.status}
                    onChange={(e) => updateContractStatus(contract._id, e.target.value)}
                    className={`status-dropdown ${contract.status}`}
                    style={{ backgroundColor: getStatusColor(contract.status), color: 'white' }}
                  >
                    {contractStatuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                  <button 
                    onClick={() => openEditModal(contract)}
                    className="btn btn-small edit-contract-btn"
                  >
                    View Details
                  </button>
                </div>
              </div>
              
              <div className="contract-details">
                <p><strong>Created:</strong> {formatDate(contract.createdAt)}</p>
                {contract.signedDate && (
                  <p><strong>Signed:</strong> {formatDate(contract.signedDate)}</p>
                )}
                {contract.expiryDate && (
                  <p><strong>Expires:</strong> {formatDate(contract.expiryDate)}</p>
                )}
              </div>

              {contract.description && (
                <div className="contract-description">
                  <p><strong>Description:</strong> {contract.description}</p>
                </div>
              )}

              {contract.contractTerms && (
                <div className="contract-terms">
                  <h5>Contract Terms:</h5>
                  <div className="terms-grid">
                    {contract.contractTerms.duration && (
                      <div><strong>Duration:</strong> {contract.contractTerms.duration}</div>
                    )}
                    {contract.contractTerms.sessionFrequency && (
                      <div><strong>Frequency:</strong> {contract.contractTerms.sessionFrequency}</div>
                    )}
                    {contract.contractTerms.totalSessions && (
                      <div><strong>Total Sessions:</strong> {contract.contractTerms.totalSessions}</div>
                    )}
                    {contract.contractTerms.rate?.amount && (
                      <div><strong>Rate:</strong> ${contract.contractTerms.rate.amount} {contract.contractTerms.rate.period}</div>
                    )}
                  </div>
                </div>
              )}

              {contract.notes && (
                <div className="contract-notes">
                  <p><strong>Notes:</strong> {contract.notes}</p>
                </div>
              )}

              {contract.adminNotes && (
                <div className="admin-notes">
                  <p><strong>Admin Notes (Private):</strong> {contract.adminNotes}</p>
                </div>
              )}

              <div className="contract-file-section">
                {contract.fileName ? (
                  <div className="file-info">
                    <span className="file-icon">PDF</span>
                    <span className="file-name">{contract.fileName}</span>
                    {contract.fileSize && <span className="file-size">({formatFileSize(contract.fileSize)})</span>}
                    <div className="file-actions">
                      <button
                        className="btn btn-small btn-view-pdf"
                        onClick={() => viewContractPdf(contract._id, contract.fileName)}
                        disabled={loadingPdf}
                      >
                        {loadingPdf ? 'Loading...' : 'View PDF'}
                      </button>
                      <button
                        className="btn btn-small btn-remove-file"
                        onClick={() => deleteContractFile(contract._id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="no-file">
                    <span>No file attached</span>
                    <label className="btn btn-small btn-upload-file">
                      Upload PDF
                      <input
                        type="file"
                        accept=".pdf"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.type !== 'application/pdf') {
                              alert('Only PDF files are allowed.');
                              return;
                            }
                            if (file.size > 10 * 1024 * 1024) {
                              alert('File size must be under 10MB.');
                              return;
                            }
                            uploadContractFile(contract._id, file);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;